import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useHistory } from 'react-router-dom';
import { loginRequest } from './utils/EntraUtils/AuthConfig';
import { setCurrentUserId, setIsUserLoggedIn, setNotificationData } from '../redux/actions/uiActions';
import { setOktaUserInfo } from '../redux/actions/commonDataActions';
import { autoAssignSegment, fetchSegmentList, setIsSegmentAssigned } from '../redux/actions/segment/segmentActions';
import { parseJwt } from './utils/commonUtil';
import { getCurrentRegionAPIConfigs } from './utils/APIsUtils/httpsUtils';
import config from '../config';
import { constants } from '../constants';

const AuthInitializer = ({ children }) => {
    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const isUserLoggedIn = useSelector(state => state.uiState.isUserLoggedIn);
    const state = useSelector(state => state);
    const [isInitializing, setIsInitializing] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const currentRegionAPIConfigs = getCurrentRegionAPIConfigs();

    const isMarketSegmentPath = history.location.pathname.includes('market-segment');
    const { selectedSegment = {} } = useSelector(state => state?.segment || {});

    const handleInitError = (error, source) => {
        console.error(`AuthInitializer: ${source}`, error);
        setIsInitializing(false);
        localStorage.clear();
        sessionStorage.clear();
        instance.logoutRedirect();
    };

    const createNewUser = async (accessToken, idToken, account) => {
        const tokenData = parseJwt(accessToken);
        const name = tokenData?.name || '';
        const given_name = tokenData?.given_name || '';
        const family_name = tokenData?.family_name || '';

        const apiUrl = currentRegionAPIConfigs.baseURL + '/api/v1/users?isAdmin=true';

        try {
            const response = await fetch(apiUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                authorization: idToken,
                profile: idToken,
              },
              body: JSON.stringify({
                firstName: given_name,
                lastName: family_name,
                name: name,
                partyId: account.idTokenClaims?.nativeid,
              }),
            });

            // Handle 403 errors - logout the user
            if (response.status === 403) {
              handleInitError('User not authorized (403)', 'createNewUser API');
              return;
            }

            // Handle other error statuses
            if (!response.ok) {
              handleInitError(`API error ${response.status}`, 'createNewUser API');
              return;
            }

            const data = await response.json();

            if (config?.is_segmented && accessToken) {
              const groupsData = account.idTokenClaims?.["groups-AcademyAdmin"] || [];
              // groups-AcademyAdmin can have a string as single group or array if multiple groups are assigned
              // converting single string to array for consistency
              const groups = Array.isArray(groupsData) ? groupsData : [groupsData];

              console.log("DEBUG groups: ", groups);

              if (config?.segmentAutoAssign) {
                dispatch(autoAssignSegment({ oktaUserInfo: account, history }));
                return;
              } else if(groups.includes(constants.ADMIN_GLOBAL_USERS)) {
                history.push('/market-segments');
                return;
              }
            }

            if(config?.is_segmented && !data?.data?.segmentId) {
              if (config?.segmentAutoAssign) {
                dispatch(autoAssignSegment({ history, oktaUserInfo: account }));
                return;
              }
              history.push('/market-segments');
              return;
            } else if(config?.is_segmented && data?.data?.segmentId) {
              dispatch(fetchSegmentList(data?.data?.segmentId));
              dispatch(setIsSegmentAssigned(true));
              if(selectedSegment?.label){
                dispatch(setNotificationData({
                  show: true,
                  type: 'SUCCESS',
                  title: selectedSegment?.label ? `You are logged in to ${selectedSegment?.label}` : '',
                  description: ``,
                  }),
                );
              }
            }

            dispatch(setIsUserLoggedIn(true));

            if (data && data.data) {
              const { amwayId } = data.data;
              if (amwayId) {
                dispatch(setCurrentUserId(amwayId));
                window.localStorage.setItem('am_id', amwayId);
              }
            }
        } catch (error) {
            handleInitError(error, 'createNewUser exception');
        }
    };

    useEffect(() => {
        if (isAuthenticated && !isUserLoggedIn && !isInitializing && !isMarketSegmentPath) {
            const account = accounts[0];
            if (!account) return;

            setIsInitializing(true);
            
            instance.acquireTokenSilent({
                ...loginRequest,
                account: account
            }).then(async (response) => {
                const legacyTokenStorage = {
                    idToken: {
                        idToken: response.idToken,
                        claims: response.account.idTokenClaims,
                        expiresAt: response.account.idTokenClaims.exp
                    },
                    accessToken: {
                        accessToken: response.accessToken,
                        expiresAt: Math.floor(response.expiresOn.getTime() / 1000)
                    }
                };
                // localStorage.setItem('entra-token-storage', JSON.stringify(legacyTokenStorage));

                dispatch(setOktaUserInfo(account));

                await createNewUser(response.accessToken, response.idToken, account);
                setIsInitializing(false);
            }).catch((e) => {
                handleInitError(e, 'acquireTokenSilent failed');
            });
        }
    }, [isAuthenticated, isUserLoggedIn, instance, accounts]);

    if (isAuthenticated && !isUserLoggedIn && !isMarketSegmentPath) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: "100%" }}>Initializing session...</div>;
    }

    return children;
};

export default AuthInitializer;
